import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import CasinoSidebar from '@/components/CasinoSidebar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { LineChart, ChevronRight, TrendingUp, BarChart3, Zap, Target, DollarSign } from 'lucide-react';

export default function BankrollSimulator() {
  const [bankroll, setBankroll] = useState('500');
  const [betSize, setBetSize] = useState('10');
  const [strategy, setStrategy] = useState('flat');
  const [rtp, setRtp] = useState('96.5');
  const [numSpins, setNumSpins] = useState([1000]);
  const [compareAll, setCompareAll] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleRun = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/tools/bankroll-simulation', {
        starting_bankroll: parseFloat(bankroll), bet_size: parseFloat(betSize),
        strategy, game_rtp: parseFloat(rtp), num_spins: numSpins[0], compare_all: compareAll,
      });
      setResult(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Simulation failed'); }
    finally { setLoading(false); }
  };

  const STRAT_COLORS = { flat: '#4d7cfe', martingale: '#ef4444', fibonacci: '#f59e0b', dalembert: '#10b981', labouchere: '#8b5cf6' };
  const STRAT_NAMES = { flat: 'Flat Betting', martingale: 'Martingale', fibonacci: 'Fibonacci', dalembert: "D'Alembert", labouchere: 'Labouchere' };

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="bankroll-simulator-page">
      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6">
        <Link to="/dashboard" className="hover:text-[#4d7cfe] transition-colors">Dashboard</Link>
        <ChevronRight className="h-3.5 w-3.5" /><span className="text-[#a0a8b8]">Play Smart</span>
        <ChevronRight className="h-3.5 w-3.5" /><span className="text-white">Bankroll Simulator</span>
      </nav>
      <div className="flex items-start gap-4 mb-8 animate-fade-in">
        <div className="w-12 h-12 rounded-xl bg-[#4d7cfe]/15 flex items-center justify-center shrink-0"><LineChart className="h-6 w-6 text-[#4d7cfe]" /></div>
        <div>
          <h1 className="text-2xl lg:text-[32px] font-bold text-white" style={{ letterSpacing: '-0.005em' }}>Bankroll Simulator</h1>
          <p className="text-[#a0a8b8] text-sm mt-1">Monte Carlo simulation showing how your bankroll changes over time</p>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-[45%]">
              <div className="ciq-card">
                <form onSubmit={handleRun} className="space-y-5">
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Starting Bankroll</label>
                    <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                      <input type="number" value={bankroll} onChange={e => setBankroll(e.target.value)} required min="1"
                        className="w-full h-11 pl-8 pr-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" data-testid="sim-bankroll-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Bet Size</label>
                    <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                      <input type="number" value={betSize} onChange={e => setBetSize(e.target.value)} required min="0.1" step="0.1"
                        className="w-full h-11 pl-8 pr-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" data-testid="sim-bet-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Betting Strategy</label>
                    <Select value={strategy} onValueChange={setStrategy}>
                      <SelectTrigger className="h-11 bg-[#1a1f2e] border-white/10 text-white" data-testid="sim-strategy-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#252b3b] border-white/10">
                        {Object.entries(STRAT_NAMES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Game RTP %</label>
                    <div className="relative">
                      <input type="number" value={rtp} onChange={e => setRtp(e.target.value)} required min="80" max="100" step="0.1"
                        className="w-full h-11 px-4 pr-8 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" data-testid="sim-rtp-input" />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">%</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Number of Spins: {numSpins[0].toLocaleString()}</label>
                    <Slider value={numSpins} onValueChange={setNumSpins} min={100} max={10000} step={100} data-testid="sim-spins-slider" />
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm text-[#a0a8b8]">Compare All Strategies</label>
                    <Switch checked={compareAll} onCheckedChange={setCompareAll} className="data-[state=checked]:bg-[#4d7cfe]" data-testid="sim-compare-toggle" />
                  </div>
                  <button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-[#4d7cfe] text-white font-medium text-sm btn-lift disabled:opacity-50" data-testid="sim-run-btn">
                    {loading ? 'Running 200 Simulations...' : 'Run Simulation'}
                  </button>
                </form>
              </div>
            </div>
            <div className="lg:w-[55%]">
              {loading && <div className="space-y-4">{Array.from({length:4}).map((_,i)=><Skeleton key={i} className="h-20 w-full bg-[#2d3548] rounded-xl" />)}</div>}
              {result && !loading && (
                <div className="animate-slide-up space-y-4">
                  {Object.entries(result.strategies).map(([strat, data]) => (
                    <div key={strat} className={`ciq-card ${strat === result.primary_strategy ? 'border-[#4d7cfe]/30' : ''}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-3 h-3 rounded-full" style={{ background: STRAT_COLORS[strat] }} />
                        <h3 className="text-white font-semibold text-sm">{STRAT_NAMES[strat]}</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-lg bg-[#1a1f2e]">
                          <p className="text-xs text-[#64748b]">Avg Final Bankroll</p>
                          <p className={`text-lg font-bold ${data.pct_change >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>${data.avg_final.toLocaleString()}</p>
                          <p className="text-[10px] text-[#64748b]">{data.pct_change >= 0 ? '+' : ''}{data.pct_change}%</p>
                        </div>
                        <div className="p-3 rounded-lg bg-[#1a1f2e]">
                          <p className="text-xs text-[#64748b]">Bust Rate</p>
                          <p className={`text-lg font-bold ${data.bust_rate > 50 ? 'text-[#ef4444]' : data.bust_rate > 20 ? 'text-[#f59e0b]' : 'text-[#10b981]'}`}>{data.bust_rate}%</p>
                          {data.avg_bust_spin > 0 && <p className="text-[10px] text-[#64748b]">~spin {data.avg_bust_spin}</p>}
                        </div>
                        <div className="p-3 rounded-lg bg-[#1a1f2e]">
                          <p className="text-xs text-[#64748b]">Best Case (95th)</p>
                          <p className="text-sm font-medium text-[#10b981]">${data.best_case.toLocaleString()}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-[#1a1f2e]">
                          <p className="text-xs text-[#64748b]">Worst Case (5th)</p>
                          <p className="text-sm font-medium text-[#ef4444]">${data.worst_case.toLocaleString()}</p>
                        </div>
                      </div>
                      {/* Mini chart using CSS bars */}
                      {data.sample_runs?.[0] && (
                        <div className="mt-3 h-16 flex items-end gap-px">
                          {data.sample_runs[0].slice(0, 50).map((v, i) => {
                            const maxV = Math.max(...data.sample_runs[0]);
                            const h = maxV > 0 ? (v / maxV) * 100 : 0;
                            return <div key={i} className="flex-1 rounded-t-sm" style={{ height: `${Math.max(h, 2)}%`, background: v > parseFloat(bankroll) ? '#10b981' : '#ef4444', opacity: 0.6 }} />;
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="ciq-card">
                    <h3 className="text-white font-semibold text-sm mb-3">Key Insights</h3>
                    <ul className="space-y-2">
                      {result.insights.map((ins, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#a0a8b8]"><Target className="h-4 w-4 text-[#4d7cfe] shrink-0 mt-0.5" />{ins}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {!result && !loading && (
                <div className="ciq-card flex flex-col items-center justify-center py-12 text-center">
                  <LineChart className="h-10 w-10 text-[#3a4155] mb-3" />
                  <p className="text-[#64748b] text-sm">Configure parameters and run to see simulation results</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <CasinoSidebar sourcePage="bankroll-simulator" />
      </div>
    </div>
  );
}
